import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Drawer, Modal, Radio, Skeleton, Tag } from 'antd';
import {
  BankOutlined,
  FileTextOutlined,
  ReloadOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import useBreakpoint from '../../../../hooks/useBreakpoint';
import investorProfileService from '../../../../services/investorProfileService';
import {
  getAccountUuid,
  normalizeDocumentUrl,
  pickDefaultAccountUuid,
} from '../../../../utils/orderAccountUtils';

const BTN_BASE =
  '!h-12 !rounded-xl !text-[15px] !font-semibold !shadow-none !transition-colors';

const BTN_CANCEL = `${BTN_BASE} !border-gray-200 !bg-white !text-gray-700 hover:!border-gray-300 hover:!bg-gray-50 hover:!text-gray-900 disabled:!opacity-50`;

const BTN_PRIMARY = BTN_BASE;

function PrimaryTag() {
  return (
    <Tag color="success" className="m-0! shrink-0 text-[11px] font-medium">
      Primary
    </Tag>
  );
}

function AccountOptionCard({ selected, onSelect, children }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full rounded-xl border px-3 py-3 text-left transition-colors sm:px-3.5 ${
        selected
          ? 'border-primary bg-primary-muted ring-1 ring-primary'
          : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
      }`}
    >
      {children}
    </button>
  );
}

function BankOptionContent({ bank, selected }) {
  return (
    <div className="flex items-start gap-2.5 sm:gap-3">
      <Radio checked={selected} className="mt-0.5 shrink-0 pointer-events-none" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-sm font-semibold text-gray-900">
            {bank.bank_name || 'Bank account'}
          </span>
          {bank.is_primary ? <PrimaryTag /> : null}
          {bank.is_verified ? (
            <Tag className="m-0! shrink-0 border-0 bg-blue-50 text-[10px] text-blue-700">
              Verified
            </Tag>
          ) : null}
        </div>
        <p className="mt-1 break-all text-sm text-gray-700 tabular-nums sm:break-normal">
          {bank.account_number || '—'}
        </p>
        <p className="mt-0.5 break-words text-xs leading-relaxed text-gray-500">
          IFSC {bank.ifsc_code || '—'}
          {bank.beneficiary_name ? ` · ${bank.beneficiary_name}` : ''}
        </p>
      </div>
    </div>
  );
}

function DematOptionContent({ demat, selected, onViewCmr }) {
  const cmrUrl = demat.cmr_document_url ? normalizeDocumentUrl(demat.cmr_document_url) : '';

  return (
    <div className="flex items-start gap-2.5 sm:gap-3">
      <Radio checked={selected} className="mt-0.5 shrink-0 pointer-events-none" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-sm font-semibold text-gray-900">
            {demat.dp_name || 'Demat account'}
          </span>
          {demat.is_primary ? <PrimaryTag /> : null}
          {demat.is_verified ? (
            <Tag className="m-0! shrink-0 border-0 bg-blue-50 text-[10px] text-blue-700">
              Verified
            </Tag>
          ) : null}
        </div>
        <p className="mt-1 break-all text-sm text-gray-700 tabular-nums sm:break-normal">
          {demat.demat_number || '—'}
        </p>
        {(demat.dp_id || demat.client_id) ? (
          <p className="mt-0.5 break-words text-xs leading-relaxed text-gray-500">
            {[demat.dp_id && `DP ${demat.dp_id}`, demat.client_id && `Client ${demat.client_id}`]
              .filter(Boolean)
              .join(' · ')}
          </p>
        ) : null}
        {cmrUrl ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onViewCmr(cmrUrl);
            }}
            className="mt-2 inline-flex max-w-full items-center gap-1 text-left text-xs font-medium text-blue-600 hover:text-blue-800"
          >
            <FileTextOutlined className="shrink-0" />
            <span className="break-words">View CMR document</span>
          </button>
        ) : (
          <p className="mt-2 text-xs text-gray-400">No CMR document on file</p>
        )}
      </div>
    </div>
  );
}

function PlaceOrderBankDematBody({
  loading,
  error,
  banks,
  demats,
  selectedBankId,
  selectedDematId,
  onSelectBank,
  onSelectDemat,
  onRetry,
  onViewCmr,
}) {
  return (
    <>
      {loading ? (
        <div className="space-y-4">
          <Skeleton active paragraph={{ rows: 2 }} />
          <Skeleton active paragraph={{ rows: 2 }} />
        </div>
      ) : null}

      {!loading && error ? (
        <Alert
          type="error"
          showIcon
          message={error}
          className="mb-4 [&_.ant-alert-action]:ms-0 [&_.ant-alert-action]:mt-2 sm:[&_.ant-alert-action]:ms-2 sm:[&_.ant-alert-action]:mt-0"
          action={
            <Button size="small" type="link" icon={<ReloadOutlined />} onClick={onRetry}>
              Retry
            </Button>
          }
        />
      ) : null}

      {!loading && !error && banks.length === 0 ? (
        <Alert
          type="warning"
          showIcon
          message="No bank account on file"
          description="This investor needs at least one bank account before you can place an order."
          className="mb-4"
        />
      ) : null}

      {!loading && !error && demats.length === 0 ? (
        <Alert
          type="warning"
          showIcon
          message="No demat account on file"
          description="This investor needs at least one demat account before you can place an order."
          className="mb-4"
        />
      ) : null}

      {!loading && !error && banks.length > 0 ? (
        <section className="mb-5">
          <div className="mb-2.5 flex items-center gap-2">
            <BankOutlined className="shrink-0 text-gray-400" />
            <span className="text-xs font-bold uppercase tracking-wide text-gray-500">
              Bank account
            </span>
          </div>
          <div className="space-y-2">
            {banks.map((bank) => {
              const id = getAccountUuid(bank);
              return (
                <AccountOptionCard
                  key={id}
                  selected={selectedBankId === id}
                  onSelect={() => onSelectBank(id)}
                >
                  <BankOptionContent bank={bank} selected={selectedBankId === id} />
                </AccountOptionCard>
              );
            })}
          </div>
        </section>
      ) : null}

      {!loading && !error && demats.length > 0 ? (
        <section>
          <div className="mb-2.5 flex items-center gap-2">
            <WalletOutlined className="shrink-0 text-gray-400" />
            <span className="text-xs font-bold uppercase tracking-wide text-gray-500">
              Demat account
            </span>
          </div>
          <div className="space-y-2">
            {demats.map((demat) => {
              const id = getAccountUuid(demat);
              return (
                <AccountOptionCard
                  key={id}
                  selected={selectedDematId === id}
                  onSelect={() => onSelectDemat(id)}
                >
                  <DematOptionContent
                    demat={demat}
                    selected={selectedDematId === id}
                    onViewCmr={onViewCmr}
                  />
                </AccountOptionCard>
              );
            })}
          </div>
        </section>
      ) : null}
    </>
  );
}

function PlaceOrderBankDematShell({
  investorName,
  loading,
  error,
  banks,
  demats,
  selectedBankId,
  selectedDematId,
  onSelectBank,
  onSelectDemat,
  onRetry,
  onViewCmr,
  onCancel,
  onConfirm,
  canSubmit,
  confirming,
  isMobile,
}) {
  return (
    <div className="flex max-h-[min(88dvh,640px)] flex-col">
      <div className="shrink-0 border-b border-gray-100 px-4 py-3.5 sm:px-5 sm:py-4">
        <h2 className="text-base font-semibold text-gray-900">Confirm bank &amp; demat</h2>
        <p className="mt-1 text-sm leading-relaxed text-gray-500">
          Select accounts for{' '}
          <span className="font-medium text-gray-700">{investorName || 'this investor'}</span>
          . Primary accounts are pre-selected.
        </p>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-5">
        <PlaceOrderBankDematBody
          loading={loading}
          error={error}
          banks={banks}
          demats={demats}
          selectedBankId={selectedBankId}
          selectedDematId={selectedDematId}
          onSelectBank={onSelectBank}
          onSelectDemat={onSelectDemat}
          onRetry={onRetry}
          onViewCmr={onViewCmr}
        />
      </div>

      <div
        className="shrink-0 border-t border-gray-100 bg-gray-50/80 px-4 py-3 shadow-[0_-6px_16px_rgba(15,23,42,0.06)] backdrop-blur-sm sm:bg-white sm:px-5 sm:py-4 sm:shadow-none"
        style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
      >
        <div className={`flex gap-2.5 ${isMobile ? 'flex-col-reverse' : 'flex-row justify-end'}`}>
          <Button
            onClick={onCancel}
            disabled={confirming}
            block={isMobile}
            className={`${BTN_CANCEL} ${isMobile ? '' : 'min-w-[108px]!'}`}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={onConfirm}
            loading={confirming}
            disabled={!canSubmit}
            block={isMobile}
            className={`${BTN_PRIMARY} ${isMobile ? '' : 'min-w-[168px]!'}`}
          >
            Confirm & place order
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function PlaceOrderBankDematModal({
  open,
  onCancel,
  investorId,
  investorName,
  onConfirm,
  confirming = false,
}) {
  const isAboveSm = useBreakpoint('sm');
  const isMobile = !isAboveSm;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [banks, setBanks] = useState([]);
  const [demats, setDemats] = useState([]);
  const [selectedBankId, setSelectedBankId] = useState('');
  const [selectedDematId, setSelectedDematId] = useState('');

  const loadAccounts = useCallback(async () => {
    if (!investorId) return;
    setLoading(true);
    setError('');
    try {
      const data = await investorProfileService.getBankAndDematForOrder(investorId);
      const bankList = Array.isArray(data?.bank_details) ? data.bank_details : [];
      const dematList = Array.isArray(data?.demat_details) ? data.demat_details : [];
      setBanks(bankList);
      setDemats(dematList);
      setSelectedBankId(pickDefaultAccountUuid(bankList));
      setSelectedDematId(pickDefaultAccountUuid(dematList));
    } catch (e) {
      setBanks([]);
      setDemats([]);
      setError(e?.response?.data?.message || e?.message || 'Failed to load bank and demat details.');
    } finally {
      setLoading(false);
    }
  }, [investorId]);

  useEffect(() => {
    if (!open) return;
    loadAccounts();
  }, [open, loadAccounts]);

  const canSubmit = useMemo(() => {
    if (loading || error) return false;
    if (!banks.length || !demats.length) return false;
    const bankOk = banks.some((b) => getAccountUuid(b) === selectedBankId);
    const dematOk = demats.some((d) => getAccountUuid(d) === selectedDematId);
    return bankOk && dematOk;
  }, [loading, error, banks, demats, selectedBankId, selectedDematId]);

  const handleConfirm = () => {
    if (!canSubmit || confirming) return;
    onConfirm?.({ bankAccountId: selectedBankId, dematAccountId: selectedDematId });
  };

  const handleViewCmr = (url) => window.open(url, '_blank', 'noopener,noreferrer');

  const shellProps = {
    investorName,
    loading,
    error,
    banks,
    demats,
    selectedBankId,
    selectedDematId,
    onSelectBank: setSelectedBankId,
    onSelectDemat: setSelectedDematId,
    onRetry: loadAccounts,
    onViewCmr: handleViewCmr,
    onCancel,
    onConfirm: handleConfirm,
    canSubmit,
    confirming,
    isMobile,
  };

  if (isMobile) {
    return (
      <Drawer
        open={open}
        onClose={confirming ? undefined : onCancel}
        placement="bottom"
        height="auto"
        destroyOnClose
        closable={!confirming}
        maskClosable={!confirming}
        title={null}
        styles={{
          body: { padding: 0 },
          content: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: '92dvh',
          },
        }}
        className="place-order-accounts-drawer"
      >
        <div className="mx-auto mb-1 mt-2 h-1 w-10 shrink-0 rounded-full bg-gray-200 sm:hidden" aria-hidden />
        <PlaceOrderBankDematShell {...shellProps} />
      </Drawer>
    );
  }

  return (
    <Modal
      open={open}
      onCancel={confirming ? undefined : onCancel}
      title={null}
      footer={null}
      width={560}
      centered
      destroyOnClose
      className="place-order-accounts-modal"
      styles={{ body: { padding: 0 } }}
    >
      <PlaceOrderBankDematShell {...shellProps} />
    </Modal>
  );
}
